"use client";

import React from "react";
import Link from "next/link";
import { RxChevronRight } from "react-icons/rx";
import { useScrollAnimation, useStaggeredAnimation } from "@/hooks/useScrollAnimation";

export function HomeBrand() {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [gridRef, visibleCards] = useStaggeredAnimation(5, 200);

  return (
    <section id="relume" className="px-4 md:px-16 py-12 md:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Encabezado */}
        <div 
          ref={headerRef}
          className={`mx-auto mb-12 w-full max-w-2xl text-center transition-all duration-1000 ease-out ${
            headerVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="mb-2 font-semibold text-black text-base md:text-lg">Calidad</p>
          <h1 className="mb-3 text-3xl md:text-5xl font-bold text-black">Marcas que marcan</h1>
          <p className="text-black text-base md:text-lg">
            Explora nuestra amplia selección de vehículos destacados, cada uno cuidadosamente elegido para ofrecerte la mejor experiencia de conducción posible.
          </p>
        </div>
        {/* Grid principal */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Columna Izquierda */}
          <div className="flex flex-col gap-8">
            {/* Tarjeta grande KIA */}
            <div className={`flex flex-col border border-neutral-200 bg-white rounded-none overflow-hidden h-full transition-all duration-1000 ease-out ${
              visibleCards.has(0) 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="p-6 pt-10 pb-8 flex flex-col flex-grow">
                <p className="mb-1 font-semibold text-sm text-neutral-800">Marcas</p>
                <h2 className="mb-6 text-4xl font-bold text-black">KIA</h2>
                <p className="text-neutral-800 text-base mb-10 leading-relaxed">
                  La marca que inspira a los conductores a vivir experiencias únicas. Con un enfoque en la innovación y el diseño, Kia ofrece un camino hacia la emoción y la aventura. ¡Deja que la pasión por conducir te guíe con Kia!
                </p>
                <div className="flex flex-row items-center gap-4 mt-auto">
                  <Link 
                    href="/vehiculos/kia"
                    className="border border-black bg-white text-black px-8 py-3 text-sm font-medium hover:bg-neutral-100 transition-colors duration-300 text-center"
                  >
                    Ver más
                  </Link>
                  <Link href="/vehiculos" className="flex items-center gap-1 cursor-pointer hover:text-gray-600 transition-colors">
                    <span className="text-sm">Detalles</span>
                    <RxChevronRight className="text-black" />
                  </Link>
                </div>
              </div>
              <div className="bg-neutral-200 h-60 md:h-72 overflow-hidden">
                <img 
                  src="/Kia 600x300.png" 
                  alt="KIA" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                />
              </div>
            </div>
            {/* Grid de tarjetas pequeñas */}
            <div className="grid grid-cols-2 gap-8">
              {/* Lincoln */}
              <div className={`flex flex-col border border-neutral-200 bg-white rounded-none overflow-hidden h-full transition-all duration-1000 ease-out ${
                visibleCards.has(1) 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}>
                <div className="bg-neutral-200 h-32 overflow-hidden">
                  <img 
                    src="/Lincoln 200x300.png" 
                    alt="Lincoln" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  />
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-lg font-bold text-black mb-2">Lincoln</h3>
                  <p className="text-sm text-neutral-800 mb-4 flex-grow">Descubre la excelencia de Lincoln. Cada modelo está diseñado para ofrecer lujo y rendimiento.</p>
                  <Link href="/vehiculos" className="flex items-center gap-1 cursor-pointer hover:text-gray-600 transition-colors mt-auto">
                    <span className="text-sm">Explorar</span>
                    <RxChevronRight className="text-black" />
                  </Link>
                </div>
              </div>
              {/* Ford */}
              <div className={`flex flex-col border border-neutral-200 bg-white rounded-none overflow-hidden h-full transition-all duration-1000 ease-out ${
                visibleCards.has(2) 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-8'
              }`}>
                <div className="bg-neutral-200 h-32 overflow-hidden">
                  <img 
                    src="/Ford - 200x300.png" 
                    alt="Ford" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                  />
                </div>
                <div className="p-6 flex flex-col flex-grow">
                  <h3 className="text-lg font-bold text-black mb-2">Ford</h3>
                  <p className="text-sm text-neutral-800 mb-4 flex-grow">Destaca por su innovación y durabilidad. Con un legado sólido en la industria automotriz.</p>
                  <Link href="/vehiculos" className="flex items-center gap-1 cursor-pointer hover:text-gray-600 transition-colors mt-auto">
                    <span className="text-sm">Comprar</span>
                    <RxChevronRight className="text-black" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
          {/* Columna Derecha */}
          <div className="flex flex-col gap-8">
            {/* Tarjeta grande Mazda */}
            <div className={`flex flex-col md:flex-row border border-neutral-200 bg-white rounded-none overflow-hidden h-[60%] transition-all duration-1000 ease-out ${
              visibleCards.has(3) 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              {/* Imagen a la izquierda */}
              <div className="bg-neutral-200 h-32 md:h-auto md:w-1/2 overflow-hidden">
                <img 
                  src="/Mazda 300x200.png" 
                  alt="Mazda" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                />
              </div>
              {/* Texto y botón a la derecha */}
              <div className="flex flex-col justify-center p-6 md:w-1/2">
                <p className="mb-2 font-semibold text-xs text-neutral-700">Marcas</p>
                <h2 className="mb-2 text-2xl font-bold text-black">Mazda</h2>
                <p className="text-neutral-800 text-sm mb-6">
                  Con una filosofía basada en crear automóviles que encienden la pasión y la emoción. Cada detalle refleja un compromiso con la calidad y la artesanía, motivando a cada persona a disfrutar del viaje.
                </p>
                <Link 
                  href="/vehiculos"
                  className="border border-black bg-white text-black px-6 py-2 text-sm font-medium w-fit flex items-center gap-1 hover:bg-neutral-100 transition-colors duration-300"
                >
                  Consultar
                  <RxChevronRight />
                </Link>
              </div>
            </div>
            {/* Tarjeta grande Geely */}
            <div className={`flex flex-col border border-neutral-200 bg-white rounded-none overflow-hidden h-full transition-all duration-1000 ease-out ${
              visibleCards.has(4) 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}>
              <div className="p-6 pt-10 pb-8 flex flex-col flex-grow">
                <p className="mb-1 font-semibold text-sm text-neutral-800">Marcas</p>
                <h2 className="mb-6 text-4xl font-bold text-black">Geely</h2>
                <p className="text-neutral-800 text-base mb-10 leading-relaxed">
                  Una marca que trae innovación y calidad en cada uno de sus modelos, ofreciendo una experiencia única que se adapta a las necesidades de nuestros clientes, garantizando la satisfacción y el uso prolongado.
                </p>
                <div className="flex flex-row items-center gap-4 mt-auto">
                  <Link 
                    href="/vehiculos"
                    className="border border-black bg-white text-black px-8 py-3 text-sm font-medium hover:bg-neutral-100 transition-colors duration-300 text-center"
                  >
                    Unirse
                  </Link>
                  <Link href="/contactos" className="flex items-center gap-1 cursor-pointer hover:text-gray-600 transition-colors">
                    <span className="text-sm">Contactar</span>
                    <RxChevronRight className="text-black" />
                  </Link>
                </div>
              </div>
              <div className="bg-neutral-200 h-60 md:h-72 overflow-hidden">
                <img 
                  src="/Geely 600x300.png" 
                  alt="Geely" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
